import { omit } from "lodash";
import { ResultElementStateLabels } from "shared/labels";
import { N_A } from "shared/rsDictionary";
import { isUncertaintyPropertiesConfigured } from "store/cell-model/cell-model.utils";
import { CustomVariableValueStatus, getCustomVariableStatus } from "store/recipe/custom-variables.utils";
import { RecipeStepType, } from "store/recipe/recipe.types";
import { flatSteps } from "store/recipe/recipe.utils";
import { isNotEmpty } from "utils/validate";
import { InsightInputType, InsightOutputTypes, OptimizationParameterParameterType, ProcessVariableType, ResultElementParameterType, ResultElementSourceType, ResultSetElementType, TableElementSourceType, } from "../rs-workspace.types";
import { getOptimizationParameterSectionTarget, getOptimizationParameterSectionTargetSource, getResultElementParameterValues, getSelectedParameterToOptimize, MIN_FLOWS_QUANTITY, } from "./common.utils";
import { checkBasicScoreCalculationConditions, getIsTimestampFilled, isCutEventFilled, } from "./insights.utils";
import { mapSpaceViewerDataToSourceTemplate } from "./results.utils";
////////////
// COMMON //
////////////
export const isResultElementCompleted = (values, cellModels, recipes, simulations, insights, processVariables, incompleteSimulationsIds, optimizations, checkForCalculation) => {
    const { type } = { ...values };
    switch (type) {
        case ResultSetElementType.Table:
            return isTableElementCompleted(values, incompleteSimulationsIds, optimizations, insights, processVariables, simulations, cellModels, recipes);
        case ResultSetElementType.SpaceViewer:
            return isSpaceviewerCompleted(values, simulations, insights, processVariables, cellModels, recipes, incompleteSimulationsIds, checkForCalculation);
        case ResultSetElementType.ProcessDataViewer: {
            const { sources: processDataViewerSources } = values;
            if (!processDataViewerSources) {
                return false;
            }
            return processDataViewerSources.some((source) => source?.source &&
                isProcessDataViewerSourceSourceCompleted(source, insights, optimizations, processVariables, incompleteSimulationsIds, simulations, cellModels, recipes));
        }
        case ResultSetElementType.LineGraph:
            return isLineGraphElementCompleted(values?.sources, values?.parameter, processVariables, cellModels, recipes, simulations, insights, optimizations, incompleteSimulationsIds);
        default:
            return false;
    }
};
/**
 * Cell model and recipe should be completed
 * Bioreactor should be completely defined
 */
export const isSimulationCompleted = (simulation, cellModels, recipes) => {
    if (!simulation) {
        return false;
    }
    const isCellModelCompleted = cellModels.find(({ id }) => id === simulation.cellModelId)?.isCompleted;
    const isRecipeCompleted = recipes.find(({ id }) => id === simulation.recipeId)?.isCompleted;
    if (!isCellModelCompleted || !isRecipeCompleted) {
        return false;
    }
    const { cellModelId, recipeId, state: { bioreactorId, impellerId, spargerId }, } = simulation;
    return [cellModelId, recipeId, bioreactorId, impellerId, spargerId].every((item) => item !== null);
};
export const isVariantCellModelParameterCompleted = (parameter) => {
    if (!parameter.parameter.parameterId || !parameter.parameter.type) {
        return false;
    }
    return isUncertaintyPropertiesConfigured(parameter.parameter);
};
/**
 * Simulation is completed
 * All added parameters/alternative flows are completed
 * All necessary and mandatory fields are filled in
 * empty variant without any parameter/alternative is a completed variant.
 */
export const isSimulationVariantCompleted = (simulation, cellModels, recipes, variant) => {
    if (!variant) {
        return true;
    }
    if (!isSimulationCompleted(simulation, cellModels, recipes)) {
        return false;
    }
    const stepsConfig = variant?.flowConfig?.stepsConfig ?? [];
    const alternativeFlowsComplete = stepsConfig?.length === 0 ||
        variant?.flowConfig?.stepsConfig?.every(({ flowId, stepId }) => isNotEmpty(flowId) && isNotEmpty(stepId));
    const cellModelParametersComplete = (variant?.cellModelParameters ?? [])?.length === 0 ||
        variant?.cellModelParameters?.every(isVariantCellModelParameterCompleted);
    const recipeParametersComplete = (variant?.recipeParameters ?? [])?.length === 0 ||
        variant?.recipeParameters?.every(({ id, value }) => isNotEmpty(id) && isNotEmpty(value, true));
    return !!alternativeFlowsComplete && !!cellModelParametersComplete && !!recipeParametersComplete;
};
////////////////////
//    INSIGHT     //
////////////////////
/**
 * each input of inputset, which is used in completed assessments,
 * must be completed simulation + completed variant, or existed and selected process data
 */
export const isInsightInputSetCompleted = (inputSet, outputs, processVariables, incompleteSimulationIds, simulations, cellModels, recipes) => {
    if (!inputSet || !outputs) {
        return false;
    }
    const { fromIncompletedOutputs, fromCompletedOutputs } = outputs.reduce((acc, output) => {
        const { sourceA, sourceB, type } = output;
        const isCompleted = type !== null && checkBasicScoreCalculationConditions(output, processVariables, recipes);
        if (sourceA !== null &&
            !(isCompleted ? acc.fromCompletedOutputs : acc.fromIncompletedOutputs).includes(sourceA)) {
            (isCompleted ? acc.fromCompletedOutputs : acc.fromIncompletedOutputs).push(sourceA);
        }
        if (type === InsightOutputTypes.Comparison &&
            sourceB !== null &&
            !(isCompleted ? acc.fromCompletedOutputs : acc.fromIncompletedOutputs).includes(sourceB)) {
            (isCompleted ? acc.fromCompletedOutputs : acc.fromIncompletedOutputs).push(sourceB);
        }
        return acc;
    }, { fromIncompletedOutputs: [], fromCompletedOutputs: [] });
    return inputSet.inputs
        .filter(({ id }) => id !== null && (fromCompletedOutputs.includes(id) || fromIncompletedOutputs.includes(id))) // take into account only inputs that used in outputs
        .every(({ id, inputId, adjustmentId, inputType }) => {
        // detect usage of input in incompleted outputs only
        if (fromIncompletedOutputs.includes(id) && !fromCompletedOutputs.includes(id)) {
            return false;
        }
        if (isNotEmpty(inputId)) {
            // detect incomplete simulation
            if (incompleteSimulationIds.includes(inputId)) {
                return false;
            }
        }
        else {
            // check if empty input used in completed output: it is ok if empty input is not used
            return !fromCompletedOutputs.includes(id);
        }
        // detect incomplete variant
        const simulation = simulations?.find(({ id }) => id === inputId);
        const variant = simulation?.state?.adjustments?.find(({ id }) => id === adjustmentId);
        const variantComplete = isSimulationVariantCompleted(simulation, cellModels, recipes, variant);
        if (inputType === InsightInputType.SIMULATION && (!isNotEmpty(adjustmentId) || !variantComplete)) {
            return false;
        }
        return true;
    });
};
/**
 * Insight must have all completed outputs
 * Insight must have min one completed input set
 * if selectedInputSetId is provided, the selected input set must be completed
 */
export const isInsightCompleted = (insightState, processVariables, incompleteSimulationsIds, simulations, cellModels, recipes, selectedInputSetId) => {
    const { input, outputs } = { ...insightState };
    if (!input?.inputSets || !outputs) {
        return false;
    }
    const filledOutputs = outputs?.filter((item) => item.type !== null) ?? [];
    if (filledOutputs.length < 1) {
        return false;
    }
    const filledOutputsCompleted = filledOutputs.every((output) => checkBasicScoreCalculationConditions(output, processVariables, recipes));
    if (!filledOutputsCompleted) {
        return false;
    }
    if (selectedInputSetId === undefined) {
        return (input?.inputSets || []).some((inputSet) => isInsightInputSetCompleted(inputSet, outputs, processVariables, incompleteSimulationsIds, simulations, cellModels, recipes));
    }
    else {
        const selectedInputSet = input?.inputSets.find(({ id }) => selectedInputSetId === id);
        return isInsightInputSetCompleted(selectedInputSet, outputs, processVariables, incompleteSimulationsIds, simulations, cellModels, recipes);
    }
};
/**
 * Input section should be filled and min one parameter should be completed
 * and have min and max values or selected min two flows
 */
export const isOptimizationCompleted = (optimizationState, insights, processVariables, incompleteSimulationsIds, simulations, cellModels, recipes) => {
    const { parameters = [], input } = { ...optimizationState };
    const { insightId, insightInputSetId, outputId, optimizationTarget } = { ...input };
    const usedInsight = insights.find((item) => item.id === insightId);
    const usedInputSet = usedInsight?.state.input.inputSets.find(({ id }) => id === insightInputSetId);
    if ([insightId, insightInputSetId, outputId, usedInputSet, usedInsight?.state.outputs].some((field) => !isNotEmpty(field))) {
        return false;
    }
    const parametersAreCompleted = parameters.some((parametersSection) => parametersSection?.parameters?.some((parameter) => {
        const selectedInput = usedInsight?.state?.input?.inputSets
            ?.find(({ id }) => id === insightInputSetId)
            ?.inputs?.find(({ id }) => id === parametersSection.inputId);
        const targetSource = getOptimizationParameterSectionTargetSource(selectedInput, simulations);
        const target = getOptimizationParameterSectionTarget(recipes, cellModels, targetSource, optimizationTarget);
        if (target === undefined || optimizationTarget === undefined) {
            return false;
        }
        const resolvedParameter = getSelectedParameterToOptimize(parameter, target, optimizationTarget);
        switch (parameter?.type) {
            case OptimizationParameterParameterType.CELL_MODEL_OR_RECIPE:
                return (isNotEmpty(resolvedParameter?.value, true) &&
                    isNotEmpty(parameter.min) &&
                    isNotEmpty(parameter.max));
            case OptimizationParameterParameterType.ALTERNATIVE_FLOW:
                return isNotEmpty(parameter.stepId) && (parameter.flowIds ?? []).length >= MIN_FLOWS_QUANTITY;
            default:
                return false;
        }
    }));
    return (parametersAreCompleted &&
        isInsightInputSetCompleted(usedInputSet, usedInsight?.state.outputs, processVariables, incompleteSimulationsIds, simulations, cellModels, recipes));
};
export const isOptimizationPreviewCanBeCalculated = (optimizationState) => {
    const { parameters, input } = { ...optimizationState };
    const { insightId, insightInputSetId, outputId } = { ...input };
    return ([insightId, insightInputSetId, outputId].every((item) => isNotEmpty(item)) &&
        !!parameters?.length &&
        !!parameters?.every((parametersSection) => !!parametersSection.parameters?.length &&
            parametersSection?.parameters?.every((parameter) => {
                switch (parameter?.type) {
                    case OptimizationParameterParameterType.CELL_MODEL_OR_RECIPE:
                        return (isNotEmpty(parameter.min) &&
                            isNotEmpty(parameter.max) &&
                            Number(parameter.min) < Number(parameter.max));
                    case OptimizationParameterParameterType.ALTERNATIVE_FLOW:
                        return isNotEmpty(parameter.stepId) && !!parameter?.flowIds?.length;
                }
            })));
};
/**
 * Selected output (assessment) or all outputs if final score is selected should be completed.
 * Selected input set should be completed.
 */
export const isInsightParameterCompleted = (parameter, insights, incompleteSimulationsIds, processVariables, simulations, cellModels, recipes) => {
    const { insightId, inputSetId } = parameter;
    const insight = insights.find(({ id }) => id === insightId);
    if (!insight || inputSetId === null) {
        return false;
    }
    return isInsightCompleted(insight.state, processVariables, incompleteSimulationsIds, simulations, cellModels, recipes, inputSetId);
};
/**
 * Parameter should have at least one value in all targets in used simulations
 */
export const getCellModelParameterCompleteness = (parameterName, usedSimulations, cellModels) => {
    const cellModelsIds = usedSimulations.map((item) => item?.cellModelId);
    const usedCellModels = cellModels.filter(({ id }) => cellModelsIds.includes(id));
    const getParameterInstancesFn = (cellModels) => cellModels.reduce((acc, { state }) => {
        const parameterInstance = Object.keys(state.parameters)
            .reduce((acc, groupKey) => [...acc, ...state.parameters[groupKey]], [])
            ?.find((parameter) => parameterName === parameter.name);
        if (parameterInstance) {
            acc.push(parameterInstance);
        }
        return acc;
    }, []);
    const parameterInstances = getParameterInstancesFn(usedCellModels);
    if (parameterInstances.length < 1) {
        const parameterInstancesByWorkspace = getParameterInstancesFn(cellModels);
        const completedByWorkspace = parameterInstancesByWorkspace.filter(({ isComplete }) => isComplete);
        return {
            bound: false,
            set: completedByWorkspace.length > 0,
            present: parameterInstancesByWorkspace.length > 0,
        };
    }
    return {
        present: true,
        bound: true,
        set: parameterInstances.filter(({ isComplete }) => isComplete).length > 0,
    };
};
/**
 * Parameter should have at least one value in all targets in used simulations
 */
export const getRecipeParameterCompleteness = (parameterName, usedSimulations, recipes) => {
    const recipesIds = usedSimulations.map((item) => item?.recipeId);
    const usedRecipes = recipes.filter(({ id }) => recipesIds.includes(id));
    const getParameterInstancesFn = (recipes) => recipes.reduce((acc, { state: { parameters = [] } }) => {
        const parameterInstance = parameters?.find(({ name }) => parameterName === name);
        if (parameterInstance) {
            acc.push(parameterInstance);
        }
        return acc;
    }, []);
    const parameterInstances = getParameterInstancesFn(usedRecipes);
    if (parameterInstances.length < 1) {
        const parameterInstancesByWorkspace = getParameterInstancesFn(recipes);
        const valuesByWorkspace = parameterInstancesByWorkspace.filter(({ value }) => isNotEmpty(value, true));
        return {
            bound: false,
            set: valuesByWorkspace.length > 0,
            present: parameterInstancesByWorkspace.length > 0,
        };
    }
    const values = parameterInstances.filter(({ value }) => isNotEmpty(value, true));
    return { present: true, bound: true, set: values.length > 0 };
};
export const getAlternativeStepCompleteness = (stepName, usedSimulations, recipes) => {
    const recipesIds = usedSimulations.map((item) => item?.recipeId);
    const usedRecipes = recipes.filter(({ id }) => recipesIds.includes(id));
    const getStepInstanceFn = (recipes) => recipes.reduce((acc, { state: { steps = [] } }) => {
        const stepInstance = flatSteps(steps).find((step) => step.type === RecipeStepType.ALTERNATIVE && step.name === stepName);
        if (stepInstance) {
            acc.push(stepInstance);
        }
        return acc;
    }, []);
    const stepInstances = getStepInstanceFn(usedRecipes);
    if (stepInstances.length < 1) {
        return {
            bound: false,
            set: false,
            present: true,
        };
    }
    return { present: true, bound: true, set: true };
};
/**
 * source parameter should be set
 * min, max, granularity should be set and be numbers
 */
export const isParameterDimensionCompleted = ({ parameter }) => {
    if (!isNotEmpty(parameter)) {
        return false;
    }
    switch (parameter?.type) {
        case ResultElementParameterType.ALTERNATIVE_FLOW: {
            const { flowIndexes } = parameter;
            return isNotEmpty(flowIndexes) && (flowIndexes || []).length >= MIN_FLOWS_QUANTITY;
        }
        case ResultElementParameterType.RECIPE:
        case ResultElementParameterType.CELL_MODEL: {
            const { min, max, granularity } = parameter;
            return [min, max, granularity].every((field) => isNotEmpty(field) && !Number.isNaN(field));
        }
        default:
            return false;
    }
};
///////////////////
// TABLE ELEMENT //
///////////////////
/**
 * ProcessData: is always complete
 * Simulation: should not be in the list of incomplete simulations
 * Optimization: Related simulation should not be in the list of incomplete simulations + Optimization should be complete + Insight input set should be complete
 */
export const isTableElementSourceCompleted = ({ sourceId, sourceType, optimizationId, adjustmentId }, incompleteSimulationsIds, optimizations, insights, processVariables, simulations, cellModels, recipes) => {
    if (!sourceId) {
        return false;
    }
    if (sourceType === TableElementSourceType.PROCESS_DATA) {
        return true;
    }
    if (!isNotEmpty(optimizationId)) {
        const simulation = simulations?.find(({ id }) => id === sourceId);
        const variant = simulation?.state?.adjustments?.find(({ id }) => id === adjustmentId);
        return (!incompleteSimulationsIds.includes(sourceId) &&
            isSimulationVariantCompleted(simulation, cellModels, recipes, variant));
    }
    const optimization = optimizations?.find(({ id }) => optimizationId === id);
    return isOptimizationCompleted(optimization?.state || {}, insights, processVariables, incompleteSimulationsIds, simulations, cellModels, recipes);
};
/**
 * At least one completed source has to be added
 */
export const isTableElementCompleted = (element, incompleteSimulationsIds, optimizations, insights, processVariables, simulations, cellModels, recipes) => element.rows.some((row) => isTableElementSourceCompleted(row, incompleteSimulationsIds, optimizations, insights, processVariables, simulations, cellModels, recipes));
export const getTableColumnCompletenessMessage = (parameter, rows, simulations, cellModels, recipes, insights, processVariables, incompleteSimulationsIds) => {
    const usedSimulationsIds = rows.map(({ sourceId, sourceType }) => sourceType === TableElementSourceType.SIMULATION ? sourceId : undefined);
    const usedSimulations = simulations.filter(({ id }) => usedSimulationsIds.includes(id));
    if (usedSimulations.length < 1) {
        return undefined;
    }
    switch (parameter.type) {
        case ResultElementParameterType.INSIGHT:
            return isInsightParameterCompleted(parameter, insights, incompleteSimulationsIds, processVariables, simulations, cellModels, recipes)
                ? undefined
                : ResultElementStateLabels.PARAMETER_IS_INVALID;
        case ResultElementParameterType.PROCESS_VARIABLE: {
            if (parameter.parameter.type === ProcessVariableType.Custom) {
                const { customVariableId, recipeId } = {
                    ...parameter?.parameter,
                };
                const customVariable = recipes
                    .find(({ id }) => id === recipeId)
                    ?.state?.customVariables?.find(({ id }) => id === customVariableId);
                if (!customVariable) {
                    return ResultElementStateLabels.PARAMETER_IS_ABSENT.replace("#TYPE", "Custom Variable").replace("#TARGET", "Recipe");
                }
                return getCustomVariableStatus(customVariable) === CustomVariableValueStatus.Completed
                    ? undefined
                    : ResultElementStateLabels.PARAMETER_IS_INVALID;
            }
            return undefined;
        }
        case ResultElementParameterType.CELL_MODEL: {
            const { set, bound, present } = getCellModelParameterCompleteness(parameter.name, usedSimulations, cellModels);
            if (bound) {
                return set ? undefined : ResultElementStateLabels.PARAMETER_IS_INVALID;
            }
            else {
                return present
                    ? undefined
                    : ResultElementStateLabels.PARAMETER_IS_ABSENT.replace("#TYPE", "Parameter").replace("#TARGET", "Cell Model");
            }
        }
        case ResultElementParameterType.RECIPE: {
            const { set, bound, present } = getRecipeParameterCompleteness(parameter.name, usedSimulations, recipes);
            if (bound) {
                return set ? undefined : ResultElementStateLabels.PARAMETER_IS_INVALID;
            }
            else {
                return present
                    ? undefined
                    : ResultElementStateLabels.PARAMETER_IS_ABSENT.replace("#TYPE", "Parameter").replace("#TARGET", "Recipe");
            }
        }
        default:
            return undefined;
    }
};
///////////////////
// SPACE VIEWER  //
///////////////////
/**
 * all necessary and mandatory fields has to be filled
 */
export const isSpaceviewerSourceCompleted = (source, processVariables) => {
    if (source.type === ResultElementSourceType.Simulation) {
        const { variableType, customVariableId, units, timing } = source;
        const conditions = [units?.value ?? units];
        if (isNotEmpty(customVariableId)) {
            conditions.push(customVariableId?.value ?? customVariableId);
        }
        else {
            conditions.push(variableType?.value ?? variableType);
        }
        if (conditions.some((field) => !isNotEmpty(field, true))) {
            return false;
        }
        if (timing === null || !getIsTimestampFilled(timing)) {
            return false;
        }
        if (isNotEmpty(timing?.cutEvent) && !isCutEventFilled(timing?.cutEvent, processVariables)) {
            return false;
        }
    }
    else {
        const { insightId, inputSetId, outputId, isFinal } = source;
        const conditions = [insightId, inputSetId];
        if (!isFinal) {
            conditions.push(outputId);
        }
        if (conditions.some((field) => !isNotEmpty(field, true))) {
            return false;
        }
    }
    return true;
};
/**
 * selected simulation or insight has to be completed
 */
export const isSpaceviewerSourceSourceCompleted = (source, processVariables, simulations, insights, cellModels, recipes, incompleteSimulationsIds) => {
    switch (source?.type) {
        case ResultElementSourceType.Simulation: {
            const { simulationId, adjustmentId, customVariableId } = source;
            const simulation = simulations?.find((sim) => sim.id === simulationId);
            const variant = simulation?.state?.adjustments?.find(({ id }) => id === adjustmentId);
            const customVariable = (recipes ?? [])
                .find(({ id }) => id === simulation?.recipeId)
                ?.state?.customVariables?.find(({ id }) => id === customVariableId);
            const isSourceVariableCompleted = isNotEmpty(customVariable)
                ? getCustomVariableStatus(customVariable ?? {}) ===
                    CustomVariableValueStatus.Completed
                : true;
            return (isSimulationCompleted(simulation, cellModels ?? [], recipes ?? []) &&
                isSimulationVariantCompleted(simulation, cellModels ?? [], recipes ?? [], variant) &&
                isSourceVariableCompleted);
        }
        case ResultElementSourceType.Insight: {
            const { inputSetId, insightId, outputId } = source;
            const sourceInsight = insights?.find((insight) => insight.id === insightId);
            const sourceInsightInputSet = sourceInsight?.state.input.inputSets.find(({ id }) => id === inputSetId);
            const sourceOutput = sourceInsight?.state.outputs.find(({ id }) => outputId === id);
            if (!sourceInsightInputSet || (!source.isFinal && !sourceOutput)) {
                return false;
            }
            return (isInsightCompleted(sourceInsight?.state, processVariables, incompleteSimulationsIds ?? [], simulations ?? [], cellModels ?? [], recipes ?? []) &&
                isInsightInputSetCompleted(sourceInsightInputSet, sourceInsight?.state.outputs, processVariables, incompleteSimulationsIds ?? [], simulations ?? [], cellModels ?? [], recipes ?? []));
        }
        default:
            return true;
    }
};
const getSpaceviewerUsedSimulations = (sources, insights, simulations) => {
    const usedSimulationsIds = sources
        .map((source) => {
        const { type, simulationId } = source;
        if (type === ResultElementSourceType.Simulation) {
            return simulationId;
        }
        const { insightId, inputSetId, outputId, isFinal } = source;
        const insight = insights.find(({ id }) => id === insightId);
        if (!insight) {
            return false;
        }
        const inputSet = insight.state.input.inputSets.find(({ id }) => id === inputSetId);
        const outputSourcesIds = [];
        if (isFinal) {
            insight.state.outputs.forEach(({ sourceA, sourceB, type }) => {
                if (sourceA === null) {
                    return;
                }
                outputSourcesIds.push(sourceA);
                if (type === InsightOutputTypes.Comparison && sourceB !== null) {
                    outputSourcesIds.push(sourceB);
                }
            });
        }
        else {
            const { type, sourceA, sourceB } = (insight.state.outputs.find(({ id }) => outputId === id) ||
                {});
            if (sourceA === null) {
                return;
            }
            outputSourcesIds.push(sourceA);
            if (type === InsightOutputTypes.Comparison && sourceB !== null) {
                outputSourcesIds.push(sourceB);
            }
        }
        return inputSet?.inputs.reduce((acc, input) => {
            if (outputSourcesIds.includes(input.id ?? 0)) {
                return [...acc, input.inputId];
            }
            return acc;
        }, []);
    })
        .flat();
    return simulations.filter(({ id }) => usedSimulationsIds.includes(id));
};
/**
 * parameter must have at least one value in all cell models and recipes from all used sources.
 */
export const isSpaceviewerParameterCompleted = (spaceviewerParameter, sources, insights, simulations, cellModels, recipes) => !getCombinationParameterCompletenessMessage(spaceviewerParameter, sources, insights, simulations, cellModels, recipes);
export const getCombinationParameterCompletenessMessage = (spaceviewerParameter, sources, insights, simulations, cellModels, recipes) => {
    const usedSimulations = getSpaceviewerUsedSimulations(sources, insights, simulations);
    const type = spaceviewerParameter?.parameter?.type;
    const parameterName = spaceviewerParameter?.parameter?.name;
    if (!parameterName) {
        return undefined;
    }
    let completeness;
    switch (type) {
        case ResultElementParameterType.RECIPE:
            completeness = getRecipeParameterCompleteness(parameterName, usedSimulations, recipes);
            break;
        case ResultElementParameterType.CELL_MODEL:
            completeness = getCellModelParameterCompleteness(parameterName, usedSimulations, cellModels);
            break;
        case ResultElementParameterType.ALTERNATIVE_FLOW:
            completeness = getAlternativeStepCompleteness(parameterName, usedSimulations, recipes);
            break;
        default:
            break;
    }
    const { set, bound, present } = completeness || {};
    if (bound) {
        return set ? undefined : ResultElementStateLabels.PARAMETER_IS_INVALID;
    }
    else {
        return present
            ? set
                ? ResultElementStateLabels.PARAMETER_IS_N_A
                : ResultElementStateLabels.PARAMETER_IS_INVALID
            : ResultElementStateLabels.PARAMETER_IS_ABSENT.replace("#TYPE", "Parameter").replace("#TARGET", type === ResultElementParameterType.RECIPE ? "Recipe" : "Cell Model");
    }
};
/**
 * Should have min one completed source and min one completed parameter
 * Source and parameter must be connected. This check is embedded in the parameter completeness chceking
 * If check is done for calculation, the visible source should be completed
 */
export const isSpaceviewerCompleted = ({ sources, parameters }, simulations, insights, processVariables, cellModels, recipes, incompleteSimulationsIds, checkForCalculation) => {
    const sourceTemplates = sources.map((source) => mapSpaceViewerDataToSourceTemplate(source));
    if (checkForCalculation) {
        const visibleSource = sourceTemplates.find(({ view }) => !!view);
        if (!visibleSource) {
            return false;
        }
        const isVisibleSourceCompleted = isSpaceviewerSourceCompleted(visibleSource, processVariables) &&
            isSpaceviewerSourceSourceCompleted(visibleSource, processVariables, simulations, insights, cellModels, recipes, incompleteSimulationsIds);
        if (!isVisibleSourceCompleted) {
            return false;
        }
    }
    else {
        const hasOneCompletedSource = sourceTemplates.some((source) => isSpaceviewerSourceCompleted(source, processVariables) &&
            isSpaceviewerSourceSourceCompleted(source, processVariables, simulations, insights, cellModels, recipes, incompleteSimulationsIds));
        if (!hasOneCompletedSource) {
            return false;
        }
    }
    const hasOneCompletedParameter = parameters.some((parameter) => isSpaceviewerParameterCompleted(parameter, sourceTemplates, insights, simulations, cellModels, recipes) && isParameterDimensionCompleted(parameter));
    if (!hasOneCompletedParameter) {
        return false;
    }
    return true;
};
///////////////////
// LINE GRAPH    //
///////////////////
/**
 * all necessary fields are filled
 */
export const isLineGraphSourceCompleted = (source, processVariables) => {
    const isSourceRowCompleted = isProcessDataViewerSourceCompleted(omit(source, "timing"));
    const conditions = [isSourceRowCompleted];
    if (source?.source?.type === ResultElementSourceType.Simulation) {
        const { timing } = { ...source?.source };
        conditions.push(timing !== null && getIsTimestampFilled(timing));
        if (isNotEmpty(timing?.cutEvent)) {
            conditions.push(isCutEventFilled(timing?.cutEvent, processVariables));
        }
    }
    return conditions.every(Boolean);
};
/**
 * Should have min one completed source and min one completed parameter
 * If check is done for calculation, the visible source should be completed
 */
export const isLineGraphElementCompleted = (sources, parameter, processVariables, cellModels, recipes, simulations, insights, optimizations, incompleteSimulationsIds) => {
    const parameterCompleted = isParameterDimensionCompleted(parameter);
    const sourcesSources = sources?.map((item) => item.source) ?? [];
    const parameterValues = getResultElementParameterValues(parameter, sourcesSources, cellModels, recipes, simulations, insights);
    const hasMatchingParameter = parameterValues?.some(({ value }) => value !== N_A);
    return (!!sources?.some((item) => isLineGraphSourceCompleted(item, processVariables) &&
        isProcessDataViewerSourceSourceCompleted(item, insights, optimizations, processVariables, incompleteSimulationsIds, simulations, cellModels, recipes)) &&
        parameterCompleted &&
        hasMatchingParameter);
};
/////////////////////////
// PROCESSDATA VIEWER  //
/////////////////////////
/**
 * all mandatory and necessary fields are filled
 */
export const isProcessDataViewerSourceCompleted = (source) => {
    if (!source || !source?.source) {
        return false;
    }
    const { simulationId, variableType, customVariableId, units } = {
        ...source?.source,
    };
    const { processDataId, liquid } = { ...source?.source };
    const { insightId, inputSetId, output, outputId } = {
        ...source?.source,
    };
    const conditions = [];
    if (source?.source?.type === ResultElementSourceType.Simulation) {
        conditions.push(variableType || customVariableId, units, !!simulationId && simulationId !== 0);
    }
    if (source?.source?.type === ResultElementSourceType.ProcessData) {
        conditions.push(variableType || liquid, units, !!processDataId && processDataId !== 0);
    }
    if (source?.source?.type === ResultElementSourceType.Insight) {
        conditions.push(insightId, inputSetId, output?.isFinal || output?.id || outputId);
    }
    return conditions.every(Boolean);
};
/**
 * simulation, optimization or insight must be completed
 */
export const isProcessDataViewerSourceSourceCompleted = (source, insights, optimizations, processVariables, incompleteSimulationsIds, simulations, cellModels, recipes) => {
    if (!source || !source?.source) {
        return true;
    }
    if (source?.source?.type === ResultElementSourceType.ProcessData) {
        const selectedVariable = source.source.variableType ?? source.source.liquid;
        return isNotEmpty(selectedVariable);
    }
    if (source?.source?.type === ResultElementSourceType.Simulation) {
        const { simulationId, adjustmentId, optimizationId, customVariableId } = {
            ...source?.source,
        };
        if (isNotEmpty(optimizationId)) {
            const optimization = optimizations.find(({ id }) => id === optimizationId);
            if (!isOptimizationCompleted(optimization?.state || {}, insights, processVariables, incompleteSimulationsIds, simulations, cellModels, recipes)) {
                return false;
            }
        }
        const simulation = simulations?.find(({ id }) => id === simulationId);
        const variant = simulation?.state?.adjustments?.find(({ id }) => id === adjustmentId);
        const customVariable = recipes
            .find(({ id }) => id === simulation?.recipeId)
            ?.state?.customVariables?.find(({ id }) => id === customVariableId);
        const isSourceVariableCompleted = isNotEmpty(customVariable)
            ? getCustomVariableStatus(customVariable ?? {}) ===
                CustomVariableValueStatus.Completed
            : true;
        return (!!simulationId &&
            !incompleteSimulationsIds.includes(simulationId) &&
            isSimulationVariantCompleted(simulation, cellModels, recipes, variant) &&
            isSourceVariableCompleted);
    }
    if (source?.source?.type === ResultElementSourceType.Insight) {
        const { insightId, inputSetId } = { ...source?.source };
        const sourceInsight = insights.find(({ id }) => id === insightId);
        const sourceInsightInputSet = sourceInsight?.state.input.inputSets.find(({ id }) => id === inputSetId);
        return (isInsightCompleted(sourceInsight?.state, processVariables, incompleteSimulationsIds, simulations, cellModels, recipes) &&
            isInsightInputSetCompleted(sourceInsightInputSet, sourceInsight?.state.outputs, processVariables, incompleteSimulationsIds, simulations, cellModels, recipes));
    }
    return false;
};
